import { Address } from './address';
import { BaseItem, NullablePartial, PhoneNumberKind } from './common';
import { EhrInformation } from './ehrInformation';
import { SexualIdentity } from './patient/socialHistory';
import { Geometry } from './places';
import { Language } from './previsit/language';

export type { EhrInformation };

export enum Ethnicity {
  Chicano = 'CHICANO',
  Cuban = 'CUBAN',
  Declined = 'PATIENT_DECLINED',
  Hispanic = 'HISPANIC',
  Mexican = 'MEXICAN',
  NonHispanic = 'NON-HISPANIC',
  PuertoRican = 'PUERTO_RICAN',
  Unknown = 'UNKNOWN',
}

export enum Sex {
  Male = 'male',
  Female = 'female',
  Other = 'other',
}

export enum Race {
  African = 'AFRICAN',
  AfricanAmerican = 'AFRICAN_AMERICAN',
  AmericanIndian = 'AMERICAN_INDIAN',
  Asian = 'ASIAN',
  AsianIndian = 'ASIAN_INDIAN',
  Caucasian = 'CAUCASIAN',
  Chinese = 'CHINESE',
  Filipino = 'FILIPINO',
  Guamanian = 'GUAMANIAN',
  Japanese = 'JAPANESE',
  Korean = 'KOREAN',
  MiddleEasternOrNorthAfrican = 'MIDDLE_EASTERN_OR_NORTH_AFRICAN',
  Other = 'OTHER',
  PacificIslander = 'PACIFIC_ISLANDER',
  PatientDeclined = 'PATIENT_DECLINED',
  Samoan = 'SAMOAN',
  Vietnamese = 'VIETNAMESE',
  White = 'WHITE',
  Unavailable = 'UNAVAILABLE',
}

export enum InterpreterNeeded {
  Yes = 'YES',
  No = 'NO',
}

export enum VeteranStatus {
  ActiveDutyDependent = 'ACTIVE_DUTY_DEPENDENT',
  ActiveDuty = 'ACTIVE_DUTY',
  DeclineToAnswer = 'DECLINE_TO_ANSWER',
  Foreign = 'FOREIGN',
  Minor = 'MINOR',
  NationalGuard = 'NATIONAL_GUARD',
  NoNever = 'NO_NEVER',
  No = 'NO',
  Reserves = 'RESERVES',
  ReservistDependent = 'RESERVIST_DEPENDENT',
  RetiredDependent = 'RETIRED_DEPENDENT',
  Retired = 'RETIRED',
  Unavailable = 'UNAVAILABLE',
  Unknown = 'UNKNOWN',
  Veteran = 'VETERAN',
  Yes = 'YES',
}

export enum Religion {
  PreferNotToAnswer = 'PREFER_NOT_TO_ANSWER',
  Agnostic = 'AGNOSTIC',
  Christian = 'CHRISTIAN',
  Catholic = 'CATHOLIC',
  Mormon = 'MORMON',
  Jewish = 'JEWISH',
  Muslim = 'MUSLIM',
  JehovasWitness = 'JEHOVAS_WITNESS',
  Buddhist = 'BUDDHIST',
  Hindu = 'HINDU',
  ChristianScientists = 'CHRISTIAN_SCIENTISTS',
  OrthodoxJews = 'ORTHODOX_JEWS',
  SeventhDayAdventists = 'SEVENTH_DAY_ADVENTISTS',
  Amish = 'AMISH',
  Sikhs = 'SIKHS',
  Other = 'OTHER',
  None = 'NONE',
}

export enum EmergencyContactRelationship {
  AuntUncle = 'AUNT_UNCLE',
  Brother = 'BROTHER',
  Child = 'CHILD',
  Cousin = 'COUSIN',
  Daughter = 'DAUGHTER',
  DepChild = 'DEP_CHILD',
  DomesticPartnership = 'DOMESTIC_PARTNERSHIP',
  Donor = 'DONOR',
  Employee = 'EMPLOYEE',
  ExSpouse = 'EX_SPOUSE',
  Father = 'FATHER',
  FatherInLaw = 'FATHER_IN_LAW',
  FosterChild = 'FOSTER_CHILD',
  FosterParent = 'FOSTER_PARENT',
  Friend = 'FRIEND',
  GrandChild = 'GRANDCHILD',
  Granddaughter = 'GRANDDAUGHTER',
  Grandfather = 'GRANDFATHER',
  Grandmother = 'GRANDMOTHER',
  Grandparent = 'GRANDPARENT',
  Grandson = 'GRANDSON',
  GreatGrandfather = 'GREAT_GRANDFATHER',
  GreatGrandmother = 'GREAT_GRANDMOTHER',
  Guardian = 'GUARDIAN',
  InLaw = 'IN_LAW',
  LifePartner = 'LIFE_PARTNER',
  Mother = 'MOTHER',
  MotherInLaw = 'MOTHER_IN_LAW',
  Neighbor = 'NEIGHBOR',
  NieceNephew = 'NIECE_NEPHEW',
  Other = 'OTHER',
  OverageDependent = 'OVERAGE_DEPENDENT',
  Parent = 'PARENT',
  Recipient = 'RECIPIENT',
  Relative = 'RELATIVE',
  Roommate = 'ROOMMATE',
  Self = 'SELF',
  Sibling = 'SIBLING',
  SignificantOther = 'SIGNIFICANT_OTHER',
  Sister = 'SISTER',
  Son = 'SON',
  Sponsor = 'SPONSOR',
  Spouse = 'SPOUSE',
  Stepchild = 'STEPCHILD',
  Stepparent = 'STEPPARENT',
  Student = 'STUDENT',
  Ward = 'WARD',
}

export enum Pronoun {
  HeHimHis = 'HE_HIM_HIS',
  SheHerHers = 'SHE_HER_HERS',
  TheyThemTheirs = 'THEY_THEM_THEIRS',
  NoPronouns = 'NO_PRONOUNS',
  PleaseAsk = 'PLEASE_ASK',
  Other = 'OTHER',
}

export interface BasePatient {
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  phoneNumber?: string;
  cellPhoneNumber?: string;
  homePhoneNumber?: string;
  workPhoneNumber?: string;
  preferredPhoneNumber?: PhoneNumberKind;
  preferredPronoun?: Pronoun;
  sex?: Sex;
  ssn4?: string;
  email?: string;
  race?: Race[];
  raceRaw?: string[];
  preferredFirstName?: string;
  preferredLanguage?: Language;
  birthGestationalAge?: string;
  birthWeightGrams?: number;
  genderIdentity?: SexualIdentity;
  genderIdentityRaw?: string;
}

export interface Patient extends BaseItem, BasePatient {}

export type CreateValues = NullablePartial<BasePatient>;
export type UpdateValues = Partial<BasePatient>;

export type PatientWithAddressCoordinates = Patient & {
  mailingAddress?: Geometry;
  physicalAddress?: Geometry;
};

type PatientAddress = {
  [key in keyof Pick<
    Address,
    'address' | 'unitNumber' | 'city' | 'state' | 'zip'
  >]-?: string | null;
};

export interface PatientWithAddress {
  patientInformation: BasePatient;
  patientAddressMailing: PatientAddress;
  patientAddressPhysical: PatientAddress;
}

// patient uploads types
export enum UploadKind {
  InsuranceCard = 'INSURANCE_CARD',
  DriversLicense = 'DRIVERS_LICENSE',
}

export interface Upload extends BaseItem {
  patientId: string;
  gsPath?: string;
  kind: UploadKind;
  uploaded: boolean;
  uploadedTimestamp?: Date;
  deleted: boolean;
  deletedTimestamp?: Date;
}

export enum Recommendation {
  AlcoholDependence = 'ALCOHOL_DEPENDENCE',
  AmputationStatus = 'AMPUTATION_STATUS',
  AnnualWellnessVisit = 'ANNUAL_WELLNESS_VISIT',
  BenzodiazepineDependence = 'BENZODIAZEPINE_DEPENDENCE',
  CareOfOlderAdultsMedicationReconciliation = 'CARE_OF_OLDER_ADULTS_MEDICATION_RECONCILIATION',
  CareOfOlderAdultsPainScale = 'CARE_OF_OLDER_ADULTS_PAIN_SCALE',
  CervicalCancerScreening = 'CERVICAL_CANCER_SCREENING',
  ComplicatedDiabetesType1Neuropathy = 'COMPLICATED_DIABETES_TYPE_1_NEUROPATHY',
  ComplicatedDiabetesType1Retinopathy = 'COMPLICATED_DIABETES_TYPE_1_RETINOPATHY',
  ComplicatedDiabetesType2Neuropathy = 'COMPLICATED_DIABETES_TYPE_2_NEUROPATHY',
  ComplicatedDiabetesType2Retinopathy = 'COMPLICATED_DIABETES_TYPE_2_RETINOPATHY',
  CongestiveHeartFailure = 'CONGESTIVE_HEART_FAILURE',
  COPD = 'COPD',
  CT = 'CT',
  DeliveryReport = 'DELIVERY_REPORT',
  DepressionScreening = 'DEPRESSION_SCREENING',
  DiabetesEyeExam = 'DIABETES_EYE_EXAM',
  DiabetesHbA1cMonitoring = 'DIABETES_HBA1C_MONITORING',
  DiabetesNephropathyMonitoring = 'DIABETES_NEPHROPATHY_MONITORING',
  DiagnosticRadiology = 'DIAGNOSTIC_RADIOLOGY',
  EKG = 'EKG',
  HelixDNA = 'HELIX_DNA',
  Hypertension = 'HYPERTENSION',
  ImagingReport = 'IMAGING_REPORT',
  FollowUpAfterEmergencyHighRiskConditions = 'FOLLOW_UP_AFTER_EMERGENCY_HIGH_RISK_CONDITIONS',
  MedicationReconciliationPostDischarge = 'MEDICATION_RECONCILIATION_POST_DISCHARGE',
  MajorDepressiveDisorderSingleEpisodeMild = 'MAJOR_DEPRESSIVE_DISORDER_SINGLE_EPISODE_MILD',
  MRA = 'MRA',
  MRI = 'MRI',
  OmadaDiabetes = 'OMADA_DIABETES',
  OmadaPreDiabetes = 'OMADA_PRE_DIABETES',
  OpioidDependence = 'OPIOID_DEPENDENCE',
  Ostomy = 'OSTOMY',
  PrenatalAndPostPartumCare = 'PRENATAL_AND_POSTPARTUM_CARE',
  PreventiveCareBreastCancerScreeningForWomenFrom50To75 = 'PREVENTIVE_CARE_BREAST_CANCER_SCREENING_RECOMMENDED_EVERY_1_2_YEARS_FOR_WOMEN_BETWEEN_AGES 50_75_YEARS',
  PreventiveCareColonCancerScreen = 'PREVENTIVE_CARE_COLON_CANCER_SCREEN',
  PreventiveCarePapSmear = 'PREVENTIVE_CARE_PAP_SMEAR',
  PreventiveCareDEXAScreeningForPatients65AndOver = 'PREVENTIVE_CARE_DEXA_SCREENING_FOR_OSTEOPOROSIS_RECOMMENDED_FOR_PATIENTS_65_YEARS_AND_OVER',
  PreventiveCarePneumovax23Vaccine = 'PREVENTIVE_CARE_PNEUMOVAX23_VACCINE',
  PreventiveCarePrevnar13Vaccine = 'PREVENTIVE_CARE_PREVNAR13_VACCINE',
  PreventiveCareCovid19Vaccine = 'PREVENTIVE_CARE_COVID19_VACCINE',
  PreventiveCareInfluenzaVaccine = 'PREVENTIVE_CARE_INFLUENZA_VACCINE',
  TransitionOfCare = 'MEDICATION_RECONCILIATION_POST_DISCHARGE_18_YEARS_AND_OVER',
  TRCEngage = 'TRC_ENGAGE',
  WellChildVisit = 'WELL_CHILD_VISIT',
}

export enum PhoneNumberStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  New = 'NEW',
  Unsubscribed = 'UNSUBSCRIBED',
}

const phoneNumberFields = [
  'phoneNumber',
  'cellPhoneNumber',
  'homePhoneNumber',
  'workPhoneNumber',
] as const;
type PhoneNumberField = (typeof phoneNumberFields)[number];
type ContactStatusValue =
  | {
      number: string;
      normalizedNumber: string;
      status: PhoneNumberStatus;
    }
  | { number: string; status: 'INVALID' };
export type ContactStatus = Partial<
  Record<PhoneNumberField, ContactStatusValue>
> & {
  email?: string;
};
